<template>
  <div class="SmsEditFilters">
    <LoadingLayout v-if="loading"/>
    <Card v-else class="SmsEditFilters">
      <CardSection>
        <div id="BrowserCountTop">{{$t('sms.targetedSubscribers')}}: {{ targetedCount !== null ? targetedCount : '' }}<Spinner v-if="loadingCounts" size="small"/></div>
      </CardSection>
      <CardSection>
        <Stack distribution="fillEvenly" spacing="extraLoose">
          <FormLayout>
            <FormLayoutGroup>
              <Select
                :label="$t('Domain')"
                v-model="siteId"
                :placeholder="$t('Choose a domain')"
                :options="domainOptions"
                :help-text="$t('sms.filters.domainHelp')"
                :disabled="noEditing"
                :error="!siteId ? $t('sms.filters.domainError'): ''"
              />
              <TextField
                :label="$t('Language')"
                :value="domainLanguage"
                :disabled="noEditing"
                :help-text-html="$t('sms.filters.domainLanguageHelp')"
                readonly
              />
            </FormLayoutGroup>

            <FormLayoutGroup v-if="smsMessage.type === 'manual'">
              <DatePicker :label="$t('sms.filters.usersActiveFrom')" v-model="startDate" :help-text="$t('sms.filters.usersActiveFromHelp')"/>
              <DatePicker :label="$t('To')" v-model="endDate" />
            </FormLayoutGroup>

          </FormLayout>
        </Stack>

      </CardSection>
      <CardSection v-if="smsMessage.type === 'manual'">
        <Heading>{{$t('Filters')}}</Heading>
        <LoadingLayout v-if="!filterPanelLoaded" :card="false" />
        <InsightsFilterPanel v-model="filters.ui" @result="filterResult" @loaded="filterLoad" />
      </CardSection>
      <CardSection v-if="!theUser.isDemoAccount && smsMessage.status === 'draft'">
        <Button type="primary" :disabled="!siteId" @click="changePage('details')">{{$t('Save + Next')}}</Button>
      </CardSection>
    </Card>
  </div>
</template>

<script>

import _ from 'lodash'
import moment from 'moment'
import InsightsFilterPanel from '@/insights/InsightsFilterPanel.vue'

export default {
  name: 'SmsEditFilters',
  inject: ['store'],
  props: {
    smsMessage: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      siteId: null,
      siteSettings: [],
      targetedCount: null,
      loadingCounts: false,
      startDate: '',
      endDate: '',
      filters: {
        ui: [],
        server: []
      },
      copyData: null
    };
  },
  computed: {
    domainOptions() {
      return this.siteSettings.map(d => {
        return {
          label: d.domain,
          value: d.siteId
        }
      })
    },
    noEditing() {
      return this.smsMessage.type === 'manual' && this.smsMessage.status !== 'draft'
    },
    domainLanguage() {
      const siteSettings = this.siteSettings.find(d => d.siteId === this.siteId)
      if (!siteSettings) return ''
      if (!siteSettings.language) return this.$t('sms.filter.noLanguageSetMessage')
      const lang = this.store.languages.find(l => l.value === siteSettings.language)
      return lang.label
    }
  },
  mounted() {
    this.loading = true
    this.siteId = this.smsMessage.siteId
    this.filters = {
      ui: this.smsMessage.filters.ui || [],
      server: this.smsMessage.filters.server || []
    }

    if (this.smsMessage.type === 'manual') {
      this.startDate = ((this.smsMessage.segments && this.smsMessage.segments.startDate) || moment().subtract(1, 'years').format('YYYY-MM-DD 00:00:00'))
      this.endDate = ((this.smsMessage.segments && this.smsMessage.segments.endDate) || moment().format('YYYY-MM-DD 00:00:00'))
    }
    this.copyData = {
      siteId: _.cloneDeep(this.siteId),
      filters: _.cloneDeep(this.filters),
      startDate: _.cloneDeep(this.startDate),
      endDate: _.cloneDeep(this.endDate)
    }

    this.api.getSmsSiteSettings()
      .then((data) => {
        this.siteSettings = data
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.loading = false
      })

    //
    this.setupRefreshCountsDebounce()
    this.refreshCounts()

    if (this.smsMessage.type === 'manual') {
      this.$watch('siteId', this.refreshCounts)
      this.$watch('startDate', this.refreshCounts)
      this.$watch('endDate', this.refreshCounts)
    }
  },
  components: {
    InsightsFilterPanel
  },
  methods: {
    setupRefreshCountsDebounce() {
      this.refreshCountsDebounced = _.debounce(() => {
        // if no site then don't get counts
        if (!this.siteId) return null
        
        this.targetedCount = null

        const smsMessage = JSON.parse(JSON.stringify(this.smsMessage))

        smsMessage.segments = smsMessage.segments || {}
        // smsMessage.segments.browserLanguage = this.language ? [this.language] : []
        // smsMessage.segments.domain = this.domain
        smsMessage.siteId = this.siteId
        smsMessage.segments.startDate = this.startDate
        smsMessage.segments.endDate = this.endDate
        smsMessage.filters = this.filters
        

        this.loadingCounts = true
        this.api.getSmsMessageCount(smsMessage)
          .then((response) => {
            this.targetedCount = response
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.loadingCounts = false
          })
      }, 5000, {
        leading: true,
        trailing: true
      })
    },
    refreshCounts() {
      this.refreshCountsDebounced()
    },
    async changePage(nextPage, forceSave = true) {
      if (forceSave) {
        if (!this.filterPanelValid) {
          this.$ui.alert({ title: this.$t('sms.filters.fixFiltersMessage') })
          return
        }
        this.$emit('update', {
          nextPage,
          siteId: this.siteId,
          filters: this.filters,
          startDate: this.startDate,
          endDate: this.endDate
        })
      } else {
        if (!_.isEqual({
          siteId: this.siteId,
          filters: this.filters,
          startDate: this.startDate,
          endDate: this.endDate
        }, this.copyData)) {
          const res = await this.$ui.confirm({
            title: this.$t('Unsaved Changes'),
            content: this.$t('messaging.loseChangesMessage'),
            primaryActionLabel: this.$t('Discard changes'),
            primaryActionType: 'destructive',
            secondaryActionLabel: this.$t('Continue editing')
          })
          if (!res) return;
        }
        this.$emit('goToPage', nextPage)
      }
    },
    filterResult(serverRulesets) {
      this.filterPanelValid = !!serverRulesets
      if (this.filterPanelValid) {
        this.filters.server = serverRulesets
      }
    },
    filterLoad() {
      console.log('loaded insights')
      this.filterPanelLoaded = true
    }
  }
};
</script>

<style lang="scss" scoped>
  .BrowserChoice {
    .BrowserChoice-Os {
      float: left;
      padding-top: 10px;
      .Icon-Os {
        width: 30px;
        height: 30px;
        background-position: center center;
        background-repeat: no-repeat;
        background-size: contain;
        margin-bottom: -20px;
        &.macos {
          background-image: url(../assets/icon-macos.png);
        }
        &.windows {
          background-image: url(../assets/icon-windows.png);
        }
        &.android {
          background-image: url(../assets/icon-android.png);
        }
      }
      .Os-Name {
        position: relative;
        top: -5px;
        left: 40px;
      }
    }
    ul {
      padding-left: 0px;
      margin-top: 15px;
      li {
        cursor: pointer;
        list-style-type: none;
        border-bottom: 1px solid #f0f0f0;
        margin-left: 110px;
        padding-bottom: 10px;
      }
      .Check {
        .UIElement {
          width: 0px;
          float: right;
          position: relative;
          top: -12px;
          right: 7px;
        }
      }
    }
  }
  #BrowserCountTop {
    text-align: right;
    font-weight: bold;
  }
  .Icon-Browser {
    width: 20px;
    height: 20px;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: contain;
    float: left;
    margin-right: 15px;
    margin-left: 10px;
    margin-top: 12px;
    &.chrome {
      background-image: url(../assets/icon-chrome.png);
    }
    &.firefox {
      background-image: url(../assets/icon-ff.png);
    }
    &.edge {
      background-image: url(../assets/icon-edge.png);
    }
    &.general {
      background-image: url(../assets/icon-android-general.png);
    }
  }
  .Browser-Name {
    margin-top: 12px;
    display: inline-block;
  }
  .Icon-Disabled .Icon-Browser {
    filter: grayscale(100%);
  }
  .missing-welcome-messages {
    h2 {
      font-size: 100%;
    }
    & > ul {
      padding: 0px;
      margin-bottom: 0px;
    }
    & > ul ul {
      padding: 5px;
    }
    ul {
      list-style-type: none;
    }
  }
  .BroadcastMessage-Wrapper {
    cursor: pointer;
    position: relative;
    width: 240px;
    * {
      pointer-events: none;
    }
    .BroadcastMessage-Text {
      position: absolute;
      right: 60px;
    }
    .material-switch {
      float: right;
    }
  }
  .BroadcastMessage-Info {
    p {
      text-align: center;
      font-size: 120%;
      color: #999;
    }
  }
</style>
